import React, { useEffect, useState, useRef } from "react";
import { api, formatCHF, formatNum, currentMonthString, formatMonat, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2, Upload, Camera, FileText, Sparkles, Filter, Download, Zap } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  datum: new Date().toISOString().slice(0, 10),
  fahrer_id: "",
  fahrer_name: "",
  tournummer: "",
  do_nummer: "",
  fahrzeug_id: "",
  fahrzeug_kennzeichen: "",
  tourstart: "",
  tourende: "",
  arbeitszeit_stunden: 0,
  km: 0,
  verladung_15: 0,
  verladung_30: 0,
  entladung_10: 0,
  bemerkungen: "",
  upload_id: null,
  status: "offen",
  rate_stunde_kunde: 36.0,
  rate_km_kunde: 0.67,
  rate_verladung_15: 6.80,
  rate_verladung_30: 13.50,
  rate_entladung_10: 4.60,
};

function calcUmsatz(r) {
  return (
    (Number(r.arbeitszeit_stunden) || 0) * Number(r.rate_stunde_kunde || 36) +
    (Number(r.km_abgerechnet ?? r.km) || 0) * Number(r.rate_km_kunde || 0.67) +
    (Number(r.verladung_15) || 0) * Number(r.rate_verladung_15 || 6.8) +
    (Number(r.verladung_30) || 0) * Number(r.rate_verladung_30 || 13.5) +
    (Number(r.entladung_10) || 0) * Number(r.rate_entladung_10 || 4.6)
  );
}

function computeArbeitszeit(start, ende) {
  if (!start || !ende) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = ende.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => isNaN(n))) return null;
  let diff = eh * 60 + em - (sh * 60 + sm);
  if (diff < 0) diff += 24 * 60; // overnight
  return Math.round((diff / 60) * 100) / 100;
}

function hoursToHHMM(h) {
  if (h == null || isNaN(h)) return "—";
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}:${String(mins).padStart(2, "0")}`;
}

export default function Tagestouren() {
  const [monat, setMonat] = useState(currentMonthString());
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [fahrerFilter, setFahrerFilter] = useState("_all");
  const [aufwandUploading, setAufwandUploading] = useState(false);
  const [veDialogOpen, setVeDialogOpen] = useState(false);
  const [veBusy, setVeBusy] = useState(false);
  const [veBetrag, setVeBetrag] = useState("0");
  const aufwandRef = React.useRef();
  const fileRef = useRef();

  const load = async () => {
    const [r, e, v] = await Promise.all([
      api.get("/daily-reports", { params: { monat } }),
      api.get("/employees"),
      api.get("/vehicles"),
    ]);
    setList(r.data);
    setEmployees(e.data);
    setVehicles(v.data);
  };
  useEffect(() => {
    load();
  }, [monat]);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const findEmpByName = (name) => {
    if (!name) return "";
    const n = name.toLowerCase();
    return employees.find((e) => e.name.toLowerCase().includes(n) || n.includes(e.name.toLowerCase()))?.id || "";
  };
  const findVehByKennz = (k) => {
    if (!k) return "";
    return vehicles.find((v) => v.kennzeichen.toLowerCase() === k.toLowerCase())?.id || "";
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...EMPTY, ...r, fahrzeug_id: r.fahrzeug_id || "" });
    setOpen(true);
  };

  const handleUpload = async (files) => {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;
    setUploading(true);
    const successes = [];
    const errors = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      toast.info(`Verarbeite ${i + 1}/${fileList.length}: ${file.name}…`);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("run_ocr", "true");
      try {
        const r = await api.post("/daily-reports/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const ex = r.data.extracted;
        const suggested = r.data.suggested_employee;
        const payload = {
          datum: ex?.datum || new Date().toISOString().slice(0, 10),
          fahrer_id: suggested?.employee_id || (ex?.fahrer ? findEmpByName(ex.fahrer) : null),
          fahrer_name: ex?.fahrer || null,
          tournummer: ex?.tournummer || null,
          do_nummer: ex?.do_nummer || null,
          fahrzeug_id: ex?.fahrzeug ? findVehByKennz(ex.fahrzeug) || null : null,
          fahrzeug_kennzeichen: ex?.fahrzeug || null,
          tourstart: ex?.tourstart || null,
          tourende: ex?.tourende || null,
          arbeitszeit_stunden: ex?.arbeitszeit_stunden ?? 0,
          km: ex?.km ?? 0,
          verladung_15: ex?.verladung_15 ?? 0,
          verladung_30: ex?.verladung_30 ?? 0,
          entladung_10: ex?.entladung_10 ?? 0,
          bemerkungen: ex?.bemerkungen || "",
          upload_id: r.data.upload.id,
          status: "offen",
          rate_stunde_kunde: 36.0,
          rate_km_kunde: 0.67,
          rate_verladung_15: 6.8,
          rate_verladung_30: 13.5,
          rate_entladung_10: 4.6,
        };
        try {
          await api.post("/daily-reports", payload);
          successes.push({ file: file.name, match: suggested?.employee_name });
        } catch (e) {
          errors.push({ file: file.name, msg: "Speicherung fehlgeschlagen" });
        }
      } catch (e) {
        errors.push({ file: file.name, msg: e.response?.data?.detail || e.message });
      }
    }

    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";

    if (successes.length > 0) {
      toast.success(`${successes.length} Rapport(e) erfolgreich erfasst. Bitte einzeln prüfen und ergänzen.`);
    }
    if (errors.length > 0) {
      toast.error(`${errors.length} Fehler beim Verarbeiten — bitte erneut versuchen.`);
    }
    load();
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        fahrer_id: form.fahrer_id || null,
        fahrzeug_id: form.fahrzeug_id || null,
        arbeitszeit_stunden: Number(form.arbeitszeit_stunden) || 0,
        km: Number(form.km) || 0,
        verladung_15: Number(form.verladung_15) || 0,
        verladung_30: Number(form.verladung_30) || 0,
        entladung_10: Number(form.entladung_10) || 0,
        rate_stunde_kunde: Number(form.rate_stunde_kunde) || 36,
        rate_km_kunde: Number(form.rate_km_kunde) || 0.67,
        rate_verladung_15: Number(form.rate_verladung_15) || 6.8,
        rate_verladung_30: Number(form.rate_verladung_30) || 13.5,
        rate_entladung_10: Number(form.rate_entladung_10) || 4.6,
      };
      if (editing) await api.put(`/daily-reports/${editing.id}`, payload);
      else await api.post("/daily-reports", payload);

      // Learn alias if OCR provided a name and user picked an employee whose name differs
      if (form.fahrer_name && form.fahrer_id) {
        const emp = employees.find((e) => e.id === form.fahrer_id);
        if (emp && emp.name.toLowerCase() !== form.fahrer_name.toLowerCase()) {
          try {
            await api.post(`/employees/${form.fahrer_id}/alias`, { alias: form.fahrer_name });
          } catch (_) {}
        }
      }
      toast.success("Tagesrapport gespeichert");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rapport wirklich löschen?")) return;
    await api.delete(`/daily-reports/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const uploadAufwand = async (file) => {
    setAufwandUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("monat", monat);
      await api.post("/reconciliation/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info("Aufwand hochgeladen — KI-Auto-Abgleich startet…");
      const start = await api.post(`/reconciliation/${monat}/auto-match`);
      const jobId = start.data.job_id;
      const startTs = Date.now();
      while (Date.now() - startTs < 180000) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const st = await api.get(`/reconciliation/auto-match/status/${jobId}`);
          if (st.data.status === "done") {
            toast.success(`Auto-Abgleich fertig · ${st.data.invoice_lines} Positionen · ${st.data.diffs_inserted} Differenzen. Details unter Monatsabschluss.`);
            break;
          }
          if (st.data.status === "error") {
            toast.error("Auto-Abgleich fehlgeschlagen: " + (st.data.error || ""));
            break;
          }
        } catch (_) {}
      }
    } catch (e) {
      toast.error("Aufwand-Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setAufwandUploading(false);
      if (aufwandRef.current) aufwandRef.current.value = "";
    }
  };

  const downloadFahrerPdf = async () => {
    if (fahrerFilter === "_all") {
      toast.error("Bitte zuerst einen Fahrer wählen.");
      return;
    }
    // Gespeicherte V/E-Entschädigung laden und Dialog öffnen
    try {
      const r = await api.get(`/ve-compensation/${monat}/${fahrerFilter}`);
      setVeBetrag(String(r.data?.betrag ?? 0));
    } catch (_) {
      setVeBetrag("0");
    }
    setVeDialogOpen(true);
  };

  const confirmVeAndDownload = async () => {
    const veAmount = parseFloat(String(veBetrag).replace(",", ".")) || 0;
    setVeBusy(true);
    try {
      // Persistent speichern (pro Fahrer & Monat)
      await api.put(`/ve-compensation/${monat}/${fahrerFilter}`, { betrag: veAmount, bemerkung: "" });
      const r = await api.get(`/export/fahrerbestaetigung/${fahrerFilter}/${monat}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const emp = employees.find((e) => e.id === fahrerFilter);
      a.download = `Fahrerbestaetigung_${(emp?.name || "fahrer").replace(/\s+/g, "_")}_${monat}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setVeDialogOpen(false);
    } catch (e) {
      toast.error("PDF Export fehlgeschlagen");
    } finally {
      setVeBusy(false);
    }
  };

  const filteredList = fahrerFilter === "_all" ? list : list.filter((r) => r.fahrer_id === fahrerFilter);
  const totalH = filteredList.reduce((s, r) => s + (Number(r.arbeitszeit_stunden) || 0), 0);
  const totalKm = filteredList.reduce((s, r) => s + (Number(r.km) || 0), 0);
  const totalUmsatz = filteredList.reduce((s, r) => s + calcUmsatz(r), 0);

  return (
    <PageContainer>
      <PageHeader title="Tagestouren" subtitle="Tagesrapporte hochladen und prüfen" testId="tagestouren-page">
        <MonthPicker value={monat} onChange={setMonat} testId="td-month-picker" />
        <input
          type="file"
          ref={fileRef}
          className="hidden"
          multiple
          accept="image/*,application/pdf,.xlsx,.xls"
          onChange={(e) => e.target.files?.length && handleUpload(e.target.files)}
          data-testid="td-file-input"
        />
        <Button
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="td-upload-button"
          className="border-neutral-300"
        >
          {uploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Upload className="h-4 w-4 mr-2" />}
          {uploading ? "OCR läuft…" : "Foto/PDF hochladen (mehrere)"}
        </Button>
        <Button onClick={openNew} data-testid="td-new-button" className="bg-[#0055FF] hover:bg-[#0040CC]">
          <Plus className="h-4 w-4 mr-2" /> Manuell erfassen
        </Button>
        <input
          type="file"
          ref={aufwandRef}
          className="hidden"
          accept=".pdf,.xlsx,.xls,image/*"
          onChange={(e) => e.target.files?.[0] && uploadAufwand(e.target.files[0])}
          data-testid="td-aufwand-input"
        />
        <Button
          variant="outline"
          onClick={() => aufwandRef.current?.click()}
          disabled={aufwandUploading}
          data-testid="td-aufwand-button"
          className="border-neutral-300"
        >
          {aufwandUploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Zap className="h-4 w-4 mr-2" />}
          {aufwandUploading ? "Abgleich läuft…" : "Aufwand TT abgleichen"}
        </Button>
      </PageHeader>

      {/* Fahrer-Filter + Summary */}
      <Card className="border-neutral-200 bg-white p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="space-y-2 md:w-64">
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Fahrer-Filter</Label>
            <Select value={fahrerFilter} onValueChange={setFahrerFilter}>
              <SelectTrigger data-testid="td-fahrer-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Alle Fahrer</SelectItem>
                {employees.filter((e) => e.aktiv).map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-3">
            <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Anzahl</div>
              <div className="text-lg font-semibold nivara-num" data-testid="td-filter-count">{filteredList.length}</div>
            </div>
            <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Total Std</div>
              <div className="text-lg font-semibold nivara-num" data-testid="td-filter-hours">{totalH.toFixed(2)}</div>
            </div>
            <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Total KM</div>
              <div className="text-lg font-semibold nivara-num" data-testid="td-filter-km">{totalKm.toFixed(0)}</div>
            </div>
          </div>
          {fahrerFilter !== "_all" && (
            <Button
              onClick={downloadFahrerPdf}
              variant="outline"
              className="border-neutral-300"
              data-testid="td-fahrerbestaetigung-button"
            >
              <Download className="h-4 w-4 mr-2" /> Fahrerbestätigung PDF
            </Button>
          )}
        </div>
        {fahrerFilter !== "_all" && (
          <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-600">
            Berechneter Umsatz Kunde für {employees.find((e) => e.id === fahrerFilter)?.name}: <span className="font-mono font-semibold">{formatCHF(totalUmsatz)}</span>
          </div>
        )}
      </Card>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="td-table">
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Tour</TableHead>
                <TableHead>Fahrer</TableHead>
                <TableHead>Fahrzeug</TableHead>
                <TableHead className="text-right">Stunden</TableHead>
                <TableHead className="text-right">KM</TableHead>
                <TableHead className="text-right">V15</TableHead>
                <TableHead className="text-right">V30</TableHead>
                <TableHead className="text-right">E10</TableHead>
                <TableHead className="text-right">Umsatz</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredList.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center text-neutral-500 py-8">Keine Rapporte für die aktuelle Auswahl.</TableCell></TableRow>
              )}
              {filteredList.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono">{formatDatum(r.datum)}</TableCell>
                  <TableCell className="font-medium">{r.tournummer || "—"}</TableCell>
                  <TableCell>{r.fahrer_id ? empName(r.fahrer_id) : (r.fahrer_name || "—")}</TableCell>
                  <TableCell className="font-mono">{r.fahrzeug_id ? (vehicles.find(v=>v.id===r.fahrzeug_id)?.kennzeichen || "—") : (r.fahrzeug_kennzeichen || "—")}</TableCell>
                  <TableCell className="nivara-num">
                    {formatNum(r.arbeitszeit_stunden, 2)}
                    <span className="text-neutral-400 text-xs ml-1">({hoursToHHMM(r.arbeitszeit_stunden)})</span>
                  </TableCell>
                  <TableCell className="nivara-num">
                    {formatNum(r.km, 0)}
                    {r.km_abgerechnet != null && r.km_abgerechnet !== r.km && (
                      <span className="text-amber-700 text-xs ml-1">→ {formatNum(r.km_abgerechnet, 0)}</span>
                    )}
                  </TableCell>
                  <TableCell className="nivara-num">{r.verladung_15 || 0}</TableCell>
                  <TableCell className="nivara-num">{r.verladung_30 || 0}</TableCell>
                  <TableCell className="nivara-num">{r.entladung_10 || 0}</TableCell>
                  <TableCell className="nivara-num font-semibold">{formatCHF(calcUmsatz(r))}</TableCell>
                  <TableCell>
                    <Badge className={r.status === "abgerechnet" ? "bg-[#008A00] text-white" : r.status === "geprüft" ? "bg-[#0055FF] text-white" : "bg-[#FFB800] text-neutral-900"}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="td-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Tagesrapport bearbeiten" : "Tagesrapport erfassen"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Datum</Label>
              <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>Tournummer</Label>
              <Input value={form.tournummer || ""} onChange={(e) => setForm({ ...form, tournummer: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>DO-Nummer</Label>
              <Input value={form.do_nummer || ""} onChange={(e) => setForm({ ...form, do_nummer: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Fahrer</Label>
              <Select value={form.fahrer_id || "_none"} onValueChange={(v) => setForm({ ...form, fahrer_id: v === "_none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder={form.fahrer_name || "Fahrer wählen…"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Nicht zuordnen —</SelectItem>
                  {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {form.fahrer_name && !form.fahrer_id && (
                <div className="text-xs text-amber-700">OCR: "{form.fahrer_name}" — bitte Fahrer manuell wählen.</div>
              )}
            </div>
            <div className="space-y-1">
              <Label>Fahrzeug</Label>
              <Select value={form.fahrzeug_id || "_none"} onValueChange={(v) => setForm({ ...form, fahrzeug_id: v === "_none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder={form.fahrzeug_kennzeichen || "—"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Kein Fahrzeug —</SelectItem>
                  {vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Tourstart</Label><Input type="time" value={form.tourstart || ""} onChange={(e) => {
              const start = e.target.value;
              const auto = computeArbeitszeit(start, form.tourende);
              setForm({ ...form, tourstart: start, ...(auto != null ? { arbeitszeit_stunden: auto } : {}) });
            }} className="font-mono" data-testid="td-tourstart" /></div>
            <div className="space-y-1"><Label>Tourende</Label><Input type="time" value={form.tourende || ""} onChange={(e) => {
              const ende = e.target.value;
              const auto = computeArbeitszeit(form.tourstart, ende);
              setForm({ ...form, tourende: ende, ...(auto != null ? { arbeitszeit_stunden: auto } : {}) });
            }} className="font-mono" data-testid="td-tourende" /></div>
            <div className="space-y-1">
              <Label className="flex items-center justify-between">
                <span>Arbeitszeit (h)</span>
                {form.tourstart && form.tourende && (
                  <span className="text-xs text-neutral-400 font-mono">≈ {hoursToHHMM(form.arbeitszeit_stunden)}</span>
                )}
              </Label>
              <Input type="number" step="0.01" value={form.arbeitszeit_stunden} onChange={(e) => setForm({ ...form, arbeitszeit_stunden: e.target.value })} className="font-mono" data-testid="td-arbeitszeit" />
            </div>
            <div className="space-y-1">
              <Label className="flex items-center justify-between">
                <span>KM</span>
                {(() => {
                  const veh = vehicles.find((v) => v.id === form.fahrzeug_id);
                  if (veh?.ist_7days_fzg) {
                    return <span className="text-xs text-amber-700">7Days-Fzg · KM-Abzug</span>;
                  }
                  return null;
                })()}
              </Label>
              <Input type="number" step="1" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} className="font-mono" data-testid="td-km" />
            </div>
            <div className="space-y-1"><Label>Verladung 15 Min <span className="text-neutral-400 text-xs">(Anzahl)</span></Label><Input type="number" step="1" min="0" value={form.verladung_15} onChange={(e) => setForm({ ...form, verladung_15: e.target.value })} className="font-mono" /></div>
            <div className="space-y-1"><Label>Verladung 30 Min <span className="text-neutral-400 text-xs">(Anzahl)</span></Label><Input type="number" step="1" min="0" value={form.verladung_30} onChange={(e) => setForm({ ...form, verladung_30: e.target.value })} className="font-mono" /></div>
            <div className="space-y-1"><Label>Entladung 10 Min <span className="text-neutral-400 text-xs">(Anzahl)</span></Label><Input type="number" step="1" min="0" value={form.entladung_10} onChange={(e) => setForm({ ...form, entladung_10: e.target.value })} className="font-mono" /></div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["offen","geprüft","abgerechnet"].map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-3">
              <Label>Bemerkungen</Label>
              <Textarea value={form.bemerkungen || ""} onChange={(e) => setForm({ ...form, bemerkungen: e.target.value })} />
            </div>
            <Card className="sm:col-span-3 bg-neutral-50 p-3 border-neutral-200">
              <div className="text-xs uppercase tracking-[0.1em] text-neutral-500 mb-1">Berechneter Umsatz Kunde</div>
              <div className="text-xl font-semibold nivara-num">{formatCHF(calcUmsatz(form))}</div>
            </Card>
          </div>
          {form.upload_id && (
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" /> Originaldatei verknüpft (ID: {form.upload_id.slice(0,8)}…)
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="td-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V/E-Entschädigung Dialog (persistent pro Fahrer & Monat) */}
      <Dialog open={veDialogOpen} onOpenChange={(o) => !o && setVeDialogOpen(false)}>
        <DialogContent className="max-w-md" data-testid="ve-dialog">
          <DialogHeader>
            <DialogTitle>Verladung/Entladung-Entschädigung</DialogTitle>
            <DialogDescription>
              Für {employees.find((e) => e.id === fahrerFilter)?.name || "Fahrer"} · {formatMonat(monat)}.
              Der Betrag wird pro Fahrer &amp; Monat gespeichert und in der Fahrerbestätigung ausgewiesen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label>Entschädigung (CHF)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={veBetrag}
              onChange={(e) => setVeBetrag(e.target.value)}
              className="font-mono"
              data-testid="ve-betrag-input"
            />
            <div className="text-xs text-neutral-500">0 = keine Entschädigung.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVeDialogOpen(false)} disabled={veBusy}>Abbrechen</Button>
            <Button onClick={confirmVeAndDownload} disabled={veBusy} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="ve-confirm">
              {veBusy ? "Erstelle PDF…" : "Speichern & PDF erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
